import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { AudioManager } from "../audio/AudioManager";
import { MusicController } from "../audio/MusicController";
import { Camera2D } from "../engine/camera/Camera2D";
import { CanvasViewport } from "../engine/canvas/CanvasViewport";
import { loadPolandAdm1 } from "../geo/data/loadPolandAdm1";
import { loadEuropeAdm0 } from "../geo/data/loadEuropeAdm0";
import { loadContinentAdm0 } from "../geo/data/loadContinentAdm0";
import { loadWorldAdm0 } from "../geo/data/loadWorldAdm0";
import { GeoHitTest } from "../geo/hit/GeoHitTest";
import { PlanarSurface } from "../geo/projection/PlanarSurface";
import { DEFAULT_GEO_THEME } from "../geo/render/GeoTheme";
import { GeoRenderer } from "../geo/render/GeoRenderer";
import { Region, RegionId, ScreenPoint } from "../geo/types";
import { DEFAULT_QUIZ_CONFIG } from "../quiz/QuizConfig";
import { QuizEngine } from "../quiz/QuizEngine";
import type { FeatureCollection, Geometry } from "geojson";
import type { RegionProps } from "../geo/types";

type MapSelection =
  | "poland"
  | "europe"
  | "world"
  | "north-america"
  | "south-america"
  | "africa"
  | "oceania"
  | "asia";
type ContinentSelection = Exclude<MapSelection, "poland" | "europe" | "world">;
type GeoBounds = {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
};

const DEBUG_QUERY_FLAG = "e2e";

export interface GeoQuizUiState {
  targetName: string;
  scoreCorrect: number;
  scoreTotal: number;
  lastResult: "correct" | "incorrect" | null;
  loading: boolean;
  mobileIntroVisible: boolean;
  isMobile: boolean;
}

class GeoQuizController {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private viewport: CanvasViewport | null = null;
  private surface: PlanarSurface | null = null;
  private camera = new Camera2D();
  private renderer = new GeoRenderer();
  private hitTest = new GeoHitTest();
  private quiz = new QuizEngine(DEFAULT_QUIZ_CONFIG);
  private audio = new AudioManager({
    successUrl: `${process.env.PUBLIC_URL}/music/success.wav`,
    failUrl: `${process.env.PUBLIC_URL}/music/fail.wav`,
    successVolume: 0.8,
    failVolume: 0.8,
  });
  private voiceAudio: HTMLAudioElement | null = null;
  private lastSpokenTargetId: RegionId | null = null;
  private voiceTimer: number | null = null;
  private lastVoiceUrl: string | null = null;
  private voiceLog: string[] = [];
  private music = new MusicController({
    playlist: [
      `${process.env.PUBLIC_URL}/music/bg_000.mp3`,
      `${process.env.PUBLIC_URL}/music/bg_001.mp3`,
    ],
    volume: 0.4,
  });
  private regions: Region[] = [];
  private hitRegions: Region[] = [];
  private featureCollection: FeatureCollection<Geometry, RegionProps> | null =
    null;
  private hoveredId?: RegionId;
  private selectedId?: RegionId;
  private feedbackId?: RegionId;
  private feedbackKind?: "correct" | "incorrect";
  private dirty = true;
  private rafId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private disposed = false;
  private onVisibilityChange = () => {
    if (document.hidden) {
      this.music.pause();
      return;
    }
    if (this.musicPrimed) {
      this.music.resume();
    }
  };
  private dragging = false;
  private dragMoved = false;
  private dragDistance = 0;
  private lastDragPoint: ScreenPoint | null = null;
  private ignoreClick = false;
  private musicPrimed = false;
  private advanceTimer: number | null = null;
  private feedbackTimer: number | null = null;
  private onUiStateChange: (state: GeoQuizUiState) => void;
  private loading = true;
  private mapSelection: MapSelection;
  private isMobile = false;
  private mobileIntroVisible = false;
  private mobileIntroDismissed = false;
  private touchMode: "none" | "pan" | "pinch" = "none";
  private lastTouchCenter: ScreenPoint | null = null;
  private lastTouchDistance: number | null = null;
  private touchMoved = false;
  private touchDistance = 0;
  private lastTouchTime = 0;
  private europeBounds: GeoBounds = {
    minLon: -31.5,
    maxLon: 60,
    minLat: 34,
    maxLat: 72,
  };
  private continentBounds: Record<ContinentSelection, GeoBounds> = {
    "north-america": {
      minLon: -170,
      maxLon: -50,
      minLat: 5,
      maxLat: 85,
    },
    "south-america": {
      minLon: -85,
      maxLon: -30,
      minLat: -60,
      maxLat: 15,
    },
    africa: {
      minLon: -20,
      maxLon: 55,
      minLat: -35,
      maxLat: 38,
    },
    oceania: {
      minLon: 110,
      maxLon: 180,
      minLat: -50,
      maxLat: 15,
    },
    asia: {
      minLon: 25,
      maxLon: 180,
      minLat: -10,
      maxLat: 80,
    },
  };
  private continentBoundsMobile: Partial<
    Record<ContinentSelection, GeoBounds>
  > = {
    oceania: {
      minLon: 115,
      maxLon: 180,
      minLat: -50,
      maxLat: 10,
    },
  };

  constructor(
    onUiStateChange: (state: GeoQuizUiState) => void,
    mapSelection: MapSelection,
  ) {
    this.onUiStateChange = onUiStateChange;
    this.mapSelection = mapSelection;
  }

  async attach(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    if (!this.ctx) {
      throw new Error("Canvas 2D context not available");
    }
    this.viewport = new CanvasViewport(canvas);
    const { width, height } = this.viewport.size;
    this.surface = new PlanarSurface(width, height);
    this.updateMobileFlag();
    await this.loadMap();
    if (this.disposed) {
      return;
    }
    this.bindEvents();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.registerDebugApi();
    this.startRenderLoop();
    this.setupResizeObserver();
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.resizeObserver && this.canvas) {
      this.resizeObserver.unobserve(this.canvas);
    }
    if (this.canvas) {
      this.canvas.removeEventListener("wheel", this.onWheel);
      this.canvas.removeEventListener("mousemove", this.onMouseMove);
      this.canvas.removeEventListener("mousedown", this.onMouseDown);
      this.canvas.removeEventListener("mouseleave", this.onMouseLeave);
      this.canvas.removeEventListener("click", this.onClick);
      this.canvas.removeEventListener("touchstart", this.onTouchStart);
      this.canvas.removeEventListener("touchmove", this.onTouchMove);
      this.canvas.removeEventListener("touchend", this.onTouchEnd);
      this.canvas.removeEventListener("touchcancel", this.onTouchEnd);
    }
    window.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.advanceTimer) {
      window.clearTimeout(this.advanceTimer);
    }
    if (this.feedbackTimer) {
      window.clearTimeout(this.feedbackTimer);
    }
    if (this.voiceAudio) {
      this.voiceAudio.pause();
      this.voiceAudio = null;
    }
    if (this.voiceTimer) {
      window.clearTimeout(this.voiceTimer);
      this.voiceTimer = null;
    }
    this.music.stop();
    this.unregisterDebugApi();
  }

  private async loadMap(): Promise<void> {
    if (this.disposed || !this.surface || !this.viewport) {
      return;
    }
    this.emitUiState({ loading: true });
    const data =
      this.mapSelection === "poland"
        ? await loadPolandAdm1({ useSimplified: true, useTopojson: true })
        : this.mapSelection === "europe"
          ? await loadEuropeAdm0()
          : this.mapSelection === "world"
            ? await loadWorldAdm0()
            : await loadContinentAdm0(this.mapSelection);
    const { regions } = this.surface.rebuildPaths(data);
    this.regions = regions;
    this.hitRegions = this.sortRegionsForHitTest(regions);
    this.featureCollection = data.featureCollection;
    this.quiz.init(this.regions.map((region) => region.id));
    this.focusForSelection();
    this.mobileIntroDismissed = false;
    this.mobileIntroVisible = this.shouldShowMobileIntro();
    if (this.disposed) {
      return;
    }
    this.emitUiState({ loading: false });
    this.markDirty();
  }

  private emitUiState(partial: Partial<GeoQuizUiState>): void {
    if (this.disposed) {
      return;
    }
    if (typeof partial.loading === "boolean") {
      this.loading = partial.loading;
    }
    const targetId = this.quiz.getTargetId();
    const targetName =
      this.regions.find((region) => region.id === targetId)?.props.plName ?? "";
    const score = this.quiz.getScore();
    this.onUiStateChange({
      targetName,
      scoreCorrect: score.correct,
      scoreTotal: score.total,
      lastResult: this.quiz.getLastResult(),
      loading: this.loading,
      mobileIntroVisible: this.mobileIntroVisible,
      isMobile: this.isMobile,
    });
    if (
      !this.loading &&
      targetId &&
      targetName &&
      targetId !== this.lastSpokenTargetId
    ) {
      this.lastSpokenTargetId = targetId;
      this.queueTargetVoice(targetName);
    }
  }

  private bindEvents(): void {
    if (!this.canvas) {
      return;
    }
    this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.canvas.addEventListener("mousemove", this.onMouseMove);
    this.canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    this.canvas.addEventListener("mouseleave", this.onMouseLeave);
    this.canvas.addEventListener("click", this.onClick);
    this.canvas.addEventListener("touchstart", this.onTouchStart, {
      passive: false,
    });
    this.canvas.addEventListener("touchmove", this.onTouchMove, {
      passive: false,
    });
    this.canvas.addEventListener("touchend", this.onTouchEnd);
    this.canvas.addEventListener("touchcancel", this.onTouchEnd);
  }

  private setupResizeObserver(): void {
    if (!this.canvas) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.canvas);
  }

  private onResize(): void {
    if (!this.viewport || !this.surface) {
      return;
    }
    this.viewport.resize();
    const { width, height } = this.viewport.size;
    this.surface.setSize(width, height);
    this.updateMobileFlag();
    if (this.featureCollection) {
      const rebuilt = this.surface.rebuildPaths({
        featureCollection: this.featureCollection,
        regions: this.regions,
      });
      this.regions = rebuilt.regions;
      this.hitRegions = this.sortRegionsForHitTest(this.regions);
      this.focusForSelection();
    }
    this.markDirty();
  }

  private onWheel = (event: WheelEvent): void => {
    if (!this.canvas) {
      return;
    }
    event.preventDefault();
    const point = this.getPointFromEvent(event);
    const zoomFactor = Math.exp(-event.deltaY * 0.001);
    this.camera.zoomAt(point, zoomFactor);
    this.markDirty();
  };

  private onMouseMove = (event: MouseEvent): void => {
    const point = this.getPointFromEvent(event);
    if (this.dragging && this.lastDragPoint) {
      const dx = point.x - this.lastDragPoint.x;
      const dy = point.y - this.lastDragPoint.y;
      this.dragDistance += Math.hypot(dx, dy);
      if (this.dragDistance > 3) {
        this.dragMoved = true;
      }
      this.camera.panBy(dx, dy);
      this.lastDragPoint = point;
      this.markDirty();
      return;
    }

    this.updateHover(point);
  };

  private onMouseDown = (event: MouseEvent): void => {
    this.primeMusic();
    this.dragging = true;
    this.dragMoved = false;
    this.dragDistance = 0;
    this.lastDragPoint = this.getPointFromEvent(event);
  };

  private onMouseUp = (): void => {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    this.ignoreClick = this.dragMoved;
    this.lastDragPoint = null;
  };

  private onMouseLeave = (): void => {
    this.hoveredId = undefined;
    if (this.canvas) {
      this.canvas.style.cursor = "default";
    }
    this.markDirty();
  };

  private onClick = (event: MouseEvent): void => {
    this.primeMusic();
    if (Date.now() - this.lastTouchTime < 500) {
      return;
    }
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }
    if (this.mobileIntroVisible) {
      this.mobileIntroVisible = false;
      this.mobileIntroDismissed = true;
      this.emitUiState({});
      this.markDirty();
      return;
    }
    const point = this.getPointFromEvent(event);
    this.handleSelection(point);
  };

  private onTouchStart = (event: TouchEvent): void => {
    this.primeMusic();
    if (!this.canvas) {
      return;
    }
    event.preventDefault();
    if (this.mobileIntroVisible) {
      this.mobileIntroVisible = false;
      this.mobileIntroDismissed = true;
      this.emitUiState({});
      this.markDirty();
      return;
    }
    if (event.touches.length === 1) {
      this.touchMode = "pan";
      this.lastTouchCenter = this.getPointFromTouch(event.touches[0]);
      this.lastTouchDistance = null;
      this.touchMoved = false;
      this.touchDistance = 0;
      return;
    }
    if (event.touches.length >= 2) {
      this.touchMode = "pinch";
      const { center, distance } = this.getTouchMetrics(event.touches);
      this.lastTouchCenter = center;
      this.lastTouchDistance = distance;
      this.touchMoved = false;
    }
  };

  private onTouchMove = (event: TouchEvent): void => {
    if (!this.canvas) {
      return;
    }
    event.preventDefault();
    if (this.touchMode === "pan" && event.touches.length === 1) {
      const point = this.getPointFromTouch(event.touches[0]);
      if (this.lastTouchCenter) {
        const dx = point.x - this.lastTouchCenter.x;
        const dy = point.y - this.lastTouchCenter.y;
        this.touchDistance += Math.hypot(dx, dy);
        if (this.touchDistance > 6) {
          this.touchMoved = true;
        }
        this.camera.panBy(dx, dy);
        this.markDirty();
      }
      this.lastTouchCenter = point;
      return;
    }
    if (event.touches.length >= 2) {
      this.touchMode = "pinch";
      const { center, distance } = this.getTouchMetrics(event.touches);
      if (this.lastTouchDistance !== null) {
        const zoomFactor =
          this.lastTouchDistance > 0 ? distance / this.lastTouchDistance : 1;
        this.camera.zoomAt(center, zoomFactor);
        this.markDirty();
      }
      this.lastTouchCenter = center;
      this.lastTouchDistance = distance;
      this.touchMoved = true;
    }
  };

  private onTouchEnd = (event: TouchEvent): void => {
    event.preventDefault();
    if (event.touches.length === 1) {
      this.touchMode = "pan";
      this.lastTouchCenter = this.getPointFromTouch(event.touches[0]);
      this.lastTouchDistance = null;
      return;
    }
    if (event.touches.length === 0) {
      if (
        !this.touchMoved &&
        this.touchMode === "pan" &&
        this.lastTouchCenter
      ) {
        this.handleSelection(this.lastTouchCenter);
      }
      this.lastTouchTime = Date.now();
      this.touchMode = "none";
      this.lastTouchCenter = null;
      this.lastTouchDistance = null;
      this.touchMoved = false;
      this.touchDistance = 0;
    }
  };

  private handleSelection(point: ScreenPoint): void {
    if (!this.ctx) {
      return;
    }
    const hitOptions = this.getHitTestOptions();
    const region = this.hitTest.findRegionAt(
      this.ctx,
      point,
      this.camera,
      this.hitRegions,
      hitOptions,
    );
    if (!region) {
      return;
    }
    this.setLastSelection(region.id);

    const result = this.quiz.handleSelection(region.id);
    this.emitUiState({});

    if (result.correct) {
      this.audio.playSuccess();
      this.selectedId = region.id;
      this.clearFeedback();
      if (this.advanceTimer) {
        window.clearTimeout(this.advanceTimer);
      }
      const advanceDelay =
        this.isMobile && this.mapSelection !== "poland"
          ? 2000
          : DEFAULT_QUIZ_CONFIG.correctAdvanceDelayMs;
      this.advanceTimer = window.setTimeout(() => {
        this.selectedId = undefined;
        this.quiz.advanceTarget();
        this.mobileIntroDismissed = false;
        this.mobileIntroVisible = this.shouldShowMobileIntro();
        this.emitUiState({});
        this.markDirty();
      }, advanceDelay);
    } else {
      this.audio.playFail();
      this.feedbackId = result.targetId;
      this.feedbackKind = "correct";
      if (this.feedbackTimer) {
        window.clearTimeout(this.feedbackTimer);
      }
      this.feedbackTimer = window.setTimeout(() => {
        this.clearFeedback();
        if (!DEFAULT_QUIZ_CONFIG.repeatOnWrong) {
          this.quiz.advanceTarget();
          this.emitUiState({});
        }
        this.markDirty();
      }, DEFAULT_QUIZ_CONFIG.incorrectRevealMs);
    }

    this.markDirty();
  }

  private clearFeedback(): void {
    this.feedbackId = undefined;
    this.feedbackKind = undefined;
  }

  private updateHover(point: ScreenPoint): void {
    if (!this.ctx || !this.canvas) {
      return;
    }
    const hitOptions = this.getHitTestOptions();
    const region = this.hitTest.findRegionAt(
      this.ctx,
      point,
      this.camera,
      this.hitRegions,
      hitOptions,
    );
    const nextHoveredId = region?.id;
    if (nextHoveredId !== this.hoveredId) {
      this.hoveredId = nextHoveredId;
      this.canvas.style.cursor = region ? "pointer" : "default";
      this.markDirty();
    }
  }

  private startRenderLoop(): void {
    const renderFrame = () => {
      if (this.dirty) {
        this.render();
        this.dirty = false;
      }
      this.rafId = requestAnimationFrame(renderFrame);
    };
    this.rafId = requestAnimationFrame(renderFrame);
  }

  private render(): void {
    if (!this.ctx || !this.viewport) {
      return;
    }
    this.renderer.draw(
      this.ctx,
      this.viewport,
      this.camera,
      DEFAULT_GEO_THEME,
      this.regions,
      {
        hoveredId: this.hoveredId,
        selectedId: this.selectedId,
        feedbackId: this.feedbackId,
        feedbackKind: this.feedbackKind,
        showLabels: this.mapSelection !== "europe",
      },
    );
  }

  private markDirty(): void {
    this.dirty = true;
  }

  private getHitTestOptions():
    | {
        smallAreaThreshold: number;
        slopRadius: number;
      }
    | undefined {
    if (!this.isMobile) {
      return undefined;
    }
    const zoom = Math.max(this.camera.zoom, 0.001);
    const slopRadiusPx = this.mapSelection === "oceania" ? 18 : 14;
    const smallAreaThresholdPx = this.mapSelection === "oceania" ? 200 : 140;
    return {
      slopRadius: slopRadiusPx / zoom,
      smallAreaThreshold: smallAreaThresholdPx / (zoom * zoom),
    };
  }

  private primeMusic(): void {
    if (this.musicPrimed) {
      return;
    }
    this.musicPrimed = true;
    this.music.start();
  }

  private playTargetVoice(name: string): void {
    if (this.disposed) {
      return;
    }
    const category = this.mapSelection;
    const text = category === "poland" ? `Województwo ${name}` : name;
    const slug = this.slugify(text);
    const url = `${process.env.PUBLIC_URL}/audio/geo/${category}/${slug}.mp3`;
    if (this.voiceAudio) {
      this.voiceAudio.pause();
    }
    const audio = new Audio(url);
    audio.volume = 0.8;
    this.voiceAudio = audio;
    this.lastVoiceUrl = url;
    this.voiceLog.push(url);
    void audio.play().catch(() => undefined);
  }

  private queueTargetVoice(name: string): void {
    if (this.disposed) {
      return;
    }
    if (this.voiceTimer) {
      window.clearTimeout(this.voiceTimer);
    }
    this.voiceTimer = window.setTimeout(() => {
      if (this.disposed) {
        return;
      }
      this.playTargetVoice(name);
      this.voiceTimer = null;
    }, 200);
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/ą/g, "a")
      .replace(/ć/g, "c")
      .replace(/ę/g, "e")
      .replace(/ł/g, "l")
      .replace(/ń/g, "n")
      .replace(/ó/g, "o")
      .replace(/ś/g, "s")
      .replace(/[żź]/g, "z")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }

  private getPointFromEvent(event: MouseEvent | WheelEvent): ScreenPoint {
    if (!this.canvas) {
      return { x: 0, y: 0 };
    }
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private getPointFromTouch(touch: Touch): ScreenPoint {
    if (!this.canvas) {
      return { x: 0, y: 0 };
    }
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }

  private getTouchMetrics(touches: TouchList): {
    center: ScreenPoint;
    distance: number;
  } {
    const a = touches[0];
    const b = touches[1];
    const p1 = this.getPointFromTouch(a);
    const p2 = this.getPointFromTouch(b);
    const center = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return { center, distance: Math.hypot(dx, dy) };
  }

  private focusEurope(): void {
    this.focusBounds(this.europeBounds);
  }

  private focusForSelection(): void {
    if (this.mapSelection === "europe") {
      this.focusEurope();
      return;
    }
    const bounds = this.getContinentBounds(this.mapSelection, this.isMobile);
    if (bounds) {
      this.focusBounds(bounds);
    }
  }

  private getContinentBounds(
    selection: MapSelection,
    isMobile: boolean,
  ): GeoBounds | null {
    if (
      selection === "poland" ||
      selection === "europe" ||
      selection === "world"
    ) {
      return null;
    }
    if (isMobile) {
      const mobileBounds = this.continentBoundsMobile[selection];
      if (mobileBounds) {
        return mobileBounds;
      }
    }
    return this.continentBounds[selection];
  }

  private focusBounds(bounds: GeoBounds): void {
    if (!this.surface || !this.viewport) {
      return;
    }
    const northWest = this.surface.project([bounds.minLon, bounds.maxLat]);
    const southEast = this.surface.project([bounds.maxLon, bounds.minLat]);
    const projectedBounds = {
      minX: Math.min(northWest[0], southEast[0]),
      minY: Math.min(northWest[1], southEast[1]),
      maxX: Math.max(northWest[0], southEast[0]),
      maxY: Math.max(northWest[1], southEast[1]),
    };
    const padding = 24;
    const width = Math.max(1, projectedBounds.maxX - projectedBounds.minX);
    const height = Math.max(1, projectedBounds.maxY - projectedBounds.minY);
    const zoomX = (this.viewport.size.width - padding * 2) / width;
    const zoomY = (this.viewport.size.height - padding * 2) / height;
    this.camera.zoom = Math.max(
      this.camera.minZoom,
      Math.min(this.camera.maxZoom, Math.min(zoomX, zoomY)),
    );
    const contentWidth = width * this.camera.zoom;
    const contentHeight = height * this.camera.zoom;
    this.camera.offsetX =
      (this.viewport.size.width - contentWidth) / 2 -
      projectedBounds.minX * this.camera.zoom;
    this.camera.offsetY =
      (this.viewport.size.height - contentHeight) / 2 -
      projectedBounds.minY * this.camera.zoom;
  }

  private updateMobileFlag(): void {
    const nextIsMobile = window.matchMedia(
      "(max-width: 768px), (pointer: coarse)",
    ).matches;
    if (nextIsMobile === this.isMobile) {
      return;
    }
    this.isMobile = nextIsMobile;
    if (!this.isMobile) {
      this.mobileIntroVisible = false;
      this.mobileIntroDismissed = false;
    } else {
      this.mobileIntroVisible = this.shouldShowMobileIntro();
    }
    this.focusForSelection();
    this.markDirty();
    this.emitUiState({});
  }

  private shouldShowMobileIntro(): boolean {
    return (
      this.isMobile &&
      this.mapSelection !== "poland" &&
      !this.mobileIntroDismissed
    );
  }

  private registerDebugApi(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.get(DEBUG_QUERY_FLAG) !== "1") {
      return;
    }
    window.__geoQuizDebug = {
      regionIds: () => this.regions.map((region) => region.id),
      lastVoiceUrl: () => this.lastVoiceUrl,
      voiceLog: () => [...this.voiceLog],
      bboxStats: () => {
        let invalid = 0;
        let largestId = "";
        let largestArea = 0;
        for (const region of this.regions) {
          const { minX, minY, maxX, maxY } = region.bbox;
          if (
            !Number.isFinite(minX) ||
            !Number.isFinite(minY) ||
            !Number.isFinite(maxX) ||
            !Number.isFinite(maxY)
          ) {
            invalid += 1;
            continue;
          }
          const area = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
          if (area > largestArea) {
            largestArea = area;
            largestId = region.id;
          }
        }
        return {
          total: this.regions.length,
          invalid,
          largestId,
          largestArea,
        };
      },
      pickPoints: () => {
        if (!this.ctx) {
          return [];
        }
        const points: Array<{ id: string; x: number; y: number }> = [];
        for (const region of this.hitRegions) {
          const point = this.findPickPoint(region);
          if (point) {
            points.push(point);
          }
        }
        return points;
      },
      oceanPoints: (count = 5) => {
        if (!this.canvas || !this.ctx) {
          return [];
        }
        const hitOptions = this.getHitTestOptions();
        const rect = this.canvas.getBoundingClientRect();
        const points: Array<{ x: number; y: number }> = [];
        const step = 60;
        for (let y = step; y < rect.height - step; y += step) {
          for (let x = step; x < rect.width - step; x += step) {
            const region = this.hitTest.findRegionAt(
              this.ctx,
              { x, y },
              this.camera,
              this.hitRegions,
              hitOptions,
            );
            if (!region) {
              points.push({ x, y });
              if (points.length >= count) {
                return points;
              }
            }
          }
        }
        return points;
      },
      hitByCentroid: () => {
        if (!this.ctx) {
          return [];
        }
        const hitOptions = this.getHitTestOptions();
        const hits: string[] = [];
        for (const region of this.regions) {
          const screenX =
            region.centroid[0] * this.camera.zoom + this.camera.offsetX;
          const screenY =
            region.centroid[1] * this.camera.zoom + this.camera.offsetY;
          const found = this.hitTest.findRegionAt(
            this.ctx,
            { x: screenX, y: screenY },
            this.camera,
            this.hitRegions,
            hitOptions,
          );
          if (found) {
            hits.push(found.id);
          }
        }
        return hits;
      },
      hitTestAt: (x: number, y: number) => {
        if (!this.ctx) {
          return null;
        }
        const hitOptions = this.getHitTestOptions();
        const region = this.hitTest.findRegionAt(
          this.ctx,
          { x, y },
          this.camera,
          this.hitRegions,
          hitOptions,
        );
        return region?.id ?? null;
      },
      sampleRegions: (step = 80) => {
        if (!this.canvas || !this.ctx) {
          return [];
        }
        const hitOptions = this.getHitTestOptions();
        const rect = this.canvas.getBoundingClientRect();
        const ids = new Set<string>();
        for (let y = step; y < rect.height; y += step) {
          for (let x = step; x < rect.width; x += step) {
            const region = this.hitTest.findRegionAt(
              this.ctx,
              { x, y },
              this.camera,
              this.hitRegions,
              hitOptions,
            );
            if (region) {
              ids.add(region.id);
            }
          }
        }
        return Array.from(ids);
      },
      clickAt: (x: number, y: number) => {
        if (!this.ctx) {
          return null;
        }
        const hitOptions = this.getHitTestOptions();
        const region = this.hitTest.findRegionAt(
          this.ctx,
          { x, y },
          this.camera,
          this.hitRegions,
          hitOptions,
        );
        if (!region) {
          return null;
        }
        this.handleSelection({ x, y });
        this.setLastSelection(region.id);
        return region.id;
      },
    };
  }

  private unregisterDebugApi(): void {
    if (window.__geoQuizDebug) {
      delete window.__geoQuizDebug;
    }
    if (window.__geoQuizLastSelection) {
      delete window.__geoQuizLastSelection;
    }
  }

  private setLastSelection(regionId: string): void {
    if (window.__geoQuizDebug) {
      window.__geoQuizLastSelection = regionId;
    }
  }

  private findPickPoint(
    region: Region,
  ): { id: string; x: number; y: number } | null {
    if (!this.ctx) {
      return null;
    }
    const { minX, minY, maxX, maxY } = region.bbox;
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const step = Math.max(1, Math.min(8, Math.sqrt(width * height) / 6));

    const area = width * height;
    if (
      area < 25 ||
      this.ctx.isPointInPath(
        region.path,
        region.centroid[0],
        region.centroid[1],
      )
    ) {
      const screenX =
        region.centroid[0] * this.camera.zoom + this.camera.offsetX;
      const screenY =
        region.centroid[1] * this.camera.zoom + this.camera.offsetY;
      return { id: region.id, x: screenX, y: screenY };
    }

    for (let y = minY; y <= maxY; y += step) {
      for (let x = minX; x <= maxX; x += step) {
        if (this.ctx.isPointInPath(region.path, x, y)) {
          const screenX = x * this.camera.zoom + this.camera.offsetX;
          const screenY = y * this.camera.zoom + this.camera.offsetY;
          return { id: region.id, x: screenX, y: screenY };
        }
      }
    }

    for (let i = 0; i < 40; i += 1) {
      const x = minX + Math.random() * width;
      const y = minY + Math.random() * height;
      if (this.ctx.isPointInPath(region.path, x, y)) {
        const screenX = x * this.camera.zoom + this.camera.offsetX;
        const screenY = y * this.camera.zoom + this.camera.offsetY;
        return { id: region.id, x: screenX, y: screenY };
      }
    }

    const screenX = region.centroid[0] * this.camera.zoom + this.camera.offsetX;
    const screenY = region.centroid[1] * this.camera.zoom + this.camera.offsetY;
    return { id: region.id, x: screenX, y: screenY };
  }

  private sortRegionsForHitTest(regions: Region[]): Region[] {
    return [...regions].sort((a, b) => {
      const areaA =
        Math.max(0, a.bbox.maxX - a.bbox.minX) *
        Math.max(0, a.bbox.maxY - a.bbox.minY);
      const areaB =
        Math.max(0, b.bbox.maxX - b.bbox.minX) *
        Math.max(0, b.bbox.maxY - b.bbox.minY);
      return areaA - areaB;
    });
  }
}

export function useGeoQuizController(mapSelection: MapSelection): {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  uiState: GeoQuizUiState;
} {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [uiState, setUiState] = useState<GeoQuizUiState>({
    targetName: "",
    scoreCorrect: 0,
    scoreTotal: 0,
    lastResult: null,
    loading: true,
    mobileIntroVisible: false,
    isMobile: false,
  });

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const controller = new GeoQuizController(setUiState, mapSelection);
    void controller.attach(canvasRef.current);
    return () => controller.dispose();
  }, [mapSelection]);

  return { canvasRef, uiState };
}
